import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';

// Login page is NOT lazy loaded - it's the entry point and should load immediately
import Login from './pages/Login';
import TenantDiagnostic from './pages/TenantDiagnostic';

// Loading spinner component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
  </div>
);

// Lazy load all protected pages for better initial bundle size
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Models = lazy(() => import('./pages/Models'));
const CreateModel = lazy(() => import('./pages/CreateModel'));
const ModelDetail = lazy(() => import('./pages/ModelDetail'));
const Entities = lazy(() => import('./pages/Entities'));
const EntityDetail = lazy(() => import('./pages/EntityDetail'));
const StandardEntityCreation = lazy(() => import('./pages/StandardEntityCreation'));
const MasterDatasetEntityCreation = lazy(() => import('./pages/MasterDatasetEntityCreation'));
const EntityCreationWizard = lazy(() => import('./components/EntityCreationWizard'));
const MDSImport = lazy(() => import('./pages/MDSImport'));
const DataBrowser = lazy(() => import('./pages/DataBrowser'));
const DataGridPage = lazy(() => import('./pages/DataGridPage'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const Users = lazy(() => import('./pages/admin/Users'));
const Reports = lazy(() => import('./pages/admin/Reports'));
const EntityCreationOptions = lazy(() => import('./components/EntityCreationOptions'));
const IconTest = lazy(() => import('./components/IconTest'));
const SubscriptionViews = lazy(() => import('./pages/SubscriptionViews'));
const IconDebug = lazy(() => import('./components/IconDebug'));
const CSSDebug = lazy(() => import('./components/CSSDebug'));

// Placeholder pages
const Hierarchies = lazy(() => import('./pages/Hierarchies'));
const BusinessRulesOverview = lazy(() => import('./pages/business-rules/BusinessRulesOverview'));
const DataQualityOverview = lazy(() => import('./pages/data-quality/DataQualityOverview'));
const AIAgentsOverview = lazy(() => import('./pages/ai-agents/AIAgentsOverview'));

// Phase 5.1: Match & Merge
const DuplicateDetection = lazy(() => import('./pages/DuplicateDetection'));
const GoldenRecords = lazy(() => import('./pages/GoldenRecords'));
const CrossReferenceViews = lazy(() => import('./pages/CrossReferenceViews'));

// Overview pages
const ModelsOverview = lazy(() => import('./pages/ModelsOverview'));
const EntitiesOverview = lazy(() => import('./pages/EntitiesOverview'));
const DataBrowserOverview = lazy(() => import('./pages/DataBrowserOverview'));
const DashboardOverview = lazy(() => import('./pages/DashboardOverview'));
const SettingsOverview = lazy(() => import('./pages/admin/SettingsOverview'));
const UsersOverview = lazy(() => import('./pages/admin/UsersOverview'));
const ReportsOverview = lazy(() => import('./pages/admin/ReportsOverview'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const SystemMonitoring = lazy(() => import('./pages/admin/SystemMonitoring'));
const DataQuality = lazy(() => import('./pages/admin/DataQuality'));
const DatabaseMigration = lazy(() => import('./pages/admin/DatabaseMigration'));
const OrphanedMembers = lazy(() => import('./pages/admin/OrphanedMembers'));
const Members = lazy(() => import('./pages/Members'));
const MemberDetail = lazy(() => import('./pages/MemberDetail'));
const CreateMember = lazy(() => import('./pages/CreateMember'));
const EntityAttributes = lazy(() => import('./pages/EntityAttributes'));
const SQLIntegrationGuide = lazy(() => import('./pages/SQLIntegrationGuide'));
const Concepts = lazy(() => import('./pages/Concepts'));

// Phase 5: Advanced MDM Features
const MatchReview = lazy(() => import('./pages/MatchReview'));
const WorkflowManager = lazy(() => import('./pages/WorkflowManager'));
const VersionHistory = lazy(() => import('./pages/VersionHistory'));
const ReferenceDataManager = lazy(() => import('./pages/ReferenceDataManager'));
const StewardshipDashboard = lazy(() => import('./pages/StewardshipDashboard'));

// Phase 5.2-5.5: Additional Admin Pages
const Backup = lazy(() => import('./pages/admin/Backup'));
const GDPRCompliance = lazy(() => import('./pages/admin/GDPRCompliance'));
const DisasterRecovery = lazy(() => import('./pages/admin/DisasterRecovery'));
const Branding = lazy(() => import('./pages/admin/Branding'));

// Comprehensive admin pages
const BusinessMetricsDashboard = lazy(() => import('./pages/BusinessMetricsDashboard'));
const APIExplorer = lazy(() => import('./pages/admin/APIExplorer'));
const AttributeMetadataConfig = lazy(() => import('./pages/admin/AttributeMetadataConfig'));
const HealthMonitoring = lazy(() => import('./pages/admin/HealthMonitoring'));
const ReferenceDataManagement = lazy(() => import('./pages/admin/ReferenceDataManagement'));
const StewardshipTasks = lazy(() => import('./pages/admin/StewardshipTasks'));
const WorkflowManagement = lazy(() => import('./pages/admin/WorkflowManagement'));
const DemoDeployment = lazy(() => import('./pages/admin/DemoDeployment'));

function App() {
  return (
    <Router>
      <AuthProvider>
        <TenantProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes - Login is NOT lazy loaded for faster initial render */}
              <Route path="/login" element={<Login />} />
              <Route path="/tenant-diagnostic" element={<TenantDiagnostic />} />

              {/* Protected Routes - All pages are lazy loaded */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="concepts" element={<Concepts />} />
                <Route path="models" element={<Models />} />
                <Route path="models/new" element={<CreateModel />} />
                <Route path="models/:id" element={<ModelDetail />} />
                <Route path="models/:modelId/entities" element={<Entities />} />
                <Route path="entities" element={<Entities />} />
                <Route path="entities/create" element={<EntityCreationOptions />} />
                <Route path="entities/create/standard" element={<StandardEntityCreation />} />
                <Route path="entities/create/wizard" element={<EntityCreationWizard />} />
                <Route
                  path="entities/create/master-dataset"
                  element={<MasterDatasetEntityCreation />}
                />
                <Route path="entities/create/mds-import" element={<MDSImport />} />
                <Route path="entities/:entityId" element={<EntityDetail />} />
                <Route path="entities/:entityId/edit" element={<EntityDetail />} />
                <Route path="entities/:entityId/attributes" element={<EntityAttributes />} />
                <Route path="subscription-views" element={<SubscriptionViews />} />
                <Route path="members" element={<Members />} />
                <Route path="members/new" element={<CreateMember />} />
                <Route path="members/:memberId" element={<MemberDetail />} />
                <Route path="members/:memberId/edit" element={<CreateMember />} />
                <Route path="datagrid/:entityId" element={<DataGridPage />} />
                <Route path="browser" element={<DataBrowser />} />
                <Route path="browser/:entityId" element={<DataBrowser />} />

                {/* New placeholder sections */}
                <Route path="hierarchies" element={<Hierarchies />} />
                <Route path="hierarchies/*" element={<Hierarchies />} />
                <Route path="business-rules" element={<BusinessRulesOverview />} />
                <Route path="business-rules/*" element={<BusinessRulesOverview />} />
                <Route path="data-quality" element={<DataQualityOverview />} />
                <Route path="data-quality/*" element={<DataQualityOverview />} />
                <Route path="ai-agents" element={<AIAgentsOverview />} />
                <Route path="ai-agents/*" element={<AIAgentsOverview />} />

                {/* Phase 5.1: Match & Merge */}
                <Route path="match-merge" element={<DuplicateDetection />} />
                <Route path="match-merge/detect" element={<DuplicateDetection />} />
                <Route path="match-merge/golden-records" element={<GoldenRecords />} />
                <Route path="match-merge/cross-reference" element={<CrossReferenceViews />} />

                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/monitoring" element={<SystemMonitoring />} />
                <Route path="admin/health" element={<HealthMonitoring />} />
                <Route path="admin/settings" element={<Settings />} />
                <Route path="admin/branding" element={<Branding />} />
                <Route path="admin/sensitivitytypes" element={<SystemSettings />} />
                <Route path="admin/users" element={<Users />} />
                <Route path="admin/reports" element={<Reports />} />
                <Route path="admin/data-quality" element={<DataQuality />} />
                <Route path="admin/orphaned-members" element={<OrphanedMembers />} />
                <Route path="admin/database-migration" element={<DatabaseMigration />} />
                <Route path="admin/backup" element={<Backup />} />
                <Route path="admin/gdpr-compliance" element={<GDPRCompliance />} />
                <Route path="admin/disaster-recovery" element={<DisasterRecovery />} />
                <Route path="admin/api-explorer" element={<APIExplorer />} />
                <Route path="admin/attribute-metadata" element={<AttributeMetadataConfig />} />
                <Route path="admin/workflow" element={<WorkflowManagement />} />
                <Route path="admin/stewardship-tasks" element={<StewardshipTasks />} />
                <Route path="admin/reference-data" element={<ReferenceDataManagement />} />
                <Route path="admin/demo-deployment" element={<DemoDeployment />} />
                <Route path="sql-integration-guide" element={<SQLIntegrationGuide />} />
                <Route path="business-metrics" element={<BusinessMetricsDashboard />} />

                {/* Phase 5: Advanced MDM Features */}
                <Route path="match-review" element={<MatchReview />} />
                <Route path="workflow" element={<WorkflowManager />} />
                <Route path="version-history" element={<VersionHistory />} />
                <Route path="reference-data" element={<ReferenceDataManager />} />
                <Route path="stewardship" element={<StewardshipDashboard />} />

                <Route path="icon-test" element={<IconTest />} />
                <Route path="icon-debug" element={<IconDebug />} />
                <Route path="css-debug" element={<CSSDebug />} />

                {/* Overview pages for documentation */}
                <Route path="overview/dashboard" element={<DashboardOverview />} />
                <Route path="overview/models" element={<ModelsOverview />} />
                <Route path="overview/entities" element={<EntitiesOverview />} />
                <Route path="overview/data-browser" element={<DataBrowserOverview />} />
                <Route path="overview/settings" element={<SettingsOverview />} />
                <Route path="overview/users" element={<UsersOverview />} />
                <Route path="overview/reports" element={<ReportsOverview />} />
              </Route>
            </Routes>
          </Suspense>
        </TenantProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
